// T134 — accessibility helpers for the "color is never the only signal" rule
// (WCAG 1.4.1). Wrap every decorative / meaning-bearing glyph so screen readers
// hear the meaning, not the emoji. Mirrors the aria-hidden pattern already used
// in ScanHealthBanner / AiKeyBanner / EmptyState.

// <Emoji label="Cover letter ready">✍</Emoji> → the glyph is hidden from SR and
// the label (if given) is read instead. Omit `label` for purely decorative
// glyphs whose meaning is already in adjacent visible text.
export function Emoji({ label, children }) {
  return (
    <>
      <span aria-hidden="true">{children}</span>
      {label ? <span className="sr-only">{label}</span> : null}
    </>
  );
}

// <Sr>Reason: </Sr> — screen-reader-only text (visually hidden).
export function Sr({ children }) {
  return <span className="sr-only">{children}</span>;
}

// ─── T255 — the polite announcer ────────────────────────────────────────────
//
// Two of the app's primary interactions moved things without telling anyone:
// the dashboard's j/k cursor (whose only manifestation is a ring on a row) and
// a stage move in Applications (the pressed button unmounts, focus drops to
// <body>, nothing is said).
//
// A single always-mounted region, like Toasts: a polite region must be in the
// DOM *before* text lands in it, or assistive tech often announces nothing.
//
// A screen reader ignores an identical string written twice — pressing `j` past
// two rows with the same title would fall silent. The region therefore passes
// through EMPTY between messages, so the same sentence still registers as a
// change to its contents.
//
// It does NOT remount. An earlier version put a React `key` on the region for
// this, which is worse: a remounted region arrives already holding its text, and
// the paragraph above is the reason that is not announced.

import { useEffect, useState } from 'react';

const _listeners = new Set();
let _seq = 0;

/** Say something politely. No-op when no <Announcer /> is mounted. */
export function announce(message) {
  if (!message) return;
  _seq += 1;
  _listeners.forEach((fn) => fn({ message, key: _seq }));
}

// How long the region stays empty between two announcements. Long enough for
// React to commit the empty render as a separate change, short enough that the
// operator does not notice a gap.
const CLEAR_MS = 60;

export function Announcer() {
  const [message, setMessage] = useState('');
  useEffect(() => {
    const timers = new Set();
    const onAnnounce = ({ message: next }) => {
      // Blank it first, then set the text. That makes an identical sentence
      // register as a CHANGE to the region's contents, which is the only thing
      // a polite live region reacts to.
      setMessage('');
      const t = setTimeout(() => {
        timers.delete(t);
        setMessage(next);
      }, CLEAR_MS);
      timers.add(t);
    };
    _listeners.add(onAnnounce);
    return () => {
      _listeners.delete(onAnnounce);
      timers.forEach(clearTimeout);
    };
  }, []);
  // T290 tracked `key` in state and it never reached the DOM, so the bump it
  // exists for did nothing: announcing the same sentence twice — "Dismissed",
  // then "Dismissed" again — was silent the second time, exactly when the
  // operator most needs it, because he cannot see whether the keystroke landed.
  //
  // T290b — the first fix put that key on the node, which is worse than the bug.
  // A remount hands the browser a brand-new element that ALREADY contains its
  // text, and assistive technology announces changes to a region that was
  // present beforehand, not the arrival of a populated one. Repeats might have
  // started working; everything else would have stopped. The region is now
  // stable and the CONTENT is what changes, which is the documented contract.
  return (
    <div aria-live="polite" aria-atomic="true" className="sr-only">
      {message}
    </div>
  );
}
